import { useState, useRef, useEffect } from 'react';
import { db } from '../../services/firebase';
import { collection, addDoc, getDocs, deleteDoc, doc, serverTimestamp, updateDoc, orderBy, query } from 'firebase/firestore';

const DEFAULT_PROJECTS = [
  "AI 사주앱", "법안 요약앱", "골프코치앱", "IR 영문화", "ChatGPT 연동 앱 개발"
];

export function useHistoryDrawerState(history, userId, setHistory) {
  // Firestore에서 프로젝트 불러오기 (updatedAt 기준 내림차순)
  const fetchProjects = async () => {
    if (!userId) return [];
    const q = query(collection(db, `users/${userId}/projects`), orderBy('updatedAt', 'desc'));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  };

  // Firestore에 프로젝트 추가
  const addProject = async (projectName) => {
    if (!userId || !projectName.trim()) return;
    await addDoc(collection(db, `users/${userId}/projects`), {
      name: projectName.trim(),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    const projects = await fetchProjects();
    setProjects(projects);
  };

  // Firestore에서 프로젝트 이름 변경
  const updateProjectName = async (projectId, newName) => {
    if (!userId || !projectId || !newName.trim()) return;
    await updateDoc(doc(db, `users/${userId}/projects/${projectId}`), {
      name: newName.trim(),
      updatedAt: serverTimestamp()
    });
    const projects = await fetchProjects();
    setProjects(projects);
  };

  // Firestore에서 프로젝트 삭제
  const deleteProject = async (projectId) => {
    if (!userId || !projectId) return;
    await deleteDoc(doc(db, `users/${userId}/projects/${projectId}`));
    setProjects(prev => (prev || []).filter(p => p.id !== projectId));
  };

  // Firestore에서 프로젝트 목록 불러오기 (최초 1회)
  const [projects, setProjects] = useState([]);
  useEffect(() => {
    if (!userId) return setProjects([]);
    fetchProjects().then(setProjects);
  }, [userId]);

  // 프로젝트 관련 상태
  const [projectModalOpen, setProjectModalOpen] = useState(false);
  const [newProjectName, setNewProjectName] = useState("");
  const [projectHover, setProjectHover] = useState(false);
  const [projectMenuAnchor, setProjectMenuAnchor] = useState(null);
  const [projectMenuIdx, setProjectMenuIdx] = useState(-1);
  const [historyMenuAnchor, setHistoryMenuAnchor] = useState(null);
  const [historyMenuIdx, setHistoryMenuIdx] = useState(-1);
  const [projectSubMenuAnchor, setProjectSubMenuAnchor] = useState(null);
  const [projectSubMenuOpen, setProjectSubMenuOpen] = useState(false);
  const projectSubMenuRefs = useRef([]);
  const projectMenuRefs = useRef([]);
  const historyMenuRefs = useRef([]);
  const projectMenuItemRef = useRef(null);
  // 프로젝트에 추가 2차 메뉴 hover 상태
  const [projectSubMenuHover, setProjectSubMenuHover] = useState(false);
  const [projectMenuHover, setProjectMenuHover] = useState(false);
  const closeTimer = useRef();

  // 프로젝트 리스트 localStorage 연동
  useEffect(() => {
    localStorage.setItem('projects', JSON.stringify(projects));
  }, [projects]);

  // 메뉴 닫기 핸들러 (전역 클릭 시)
  useEffect(() => {
    const handleClick = (e) => {
      if (historyMenuAnchor && !historyMenuAnchor.contains(e.target) && !projectSubMenuAnchor?.contains(e.target)) {
        setHistoryMenuAnchor(null);
        setHistoryMenuIdx(-1);
        setProjectSubMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [historyMenuAnchor, projectSubMenuAnchor]);

  // 2차 메뉴 hover 제어
  useEffect(() => {
    setProjectSubMenuOpen(projectMenuHover || projectSubMenuHover);
  }, [projectMenuHover, projectSubMenuHover]);

  // 프로젝트 추가
  const handleAddProject = async () => {
    if (newProjectName.trim()) {
      await addProject(newProjectName);
      setNewProjectName("");
      setProjectModalOpen(false);
    }
  };

  // 프로젝트에 히스토리 아이템 추가 (Firestore에 projectId, updatedAt 업데이트)
  const handleAddHistoryToProject = async (projectId, historyId) => {
    if (!userId || !projectId || !historyId) return;
    const historyRef = doc(db, `users/${userId}/history/${historyId}`);
    await updateDoc(historyRef, { projectId });
    await updateDoc(doc(db, `users/${userId}/projects/${projectId}`), { updatedAt: serverTimestamp() });
    const projects = await fetchProjects();
    setProjects(projects);
    setProjectSubMenuOpen(false);
    setHistoryMenuAnchor(null);
    setHistoryMenuIdx(-1);
  };

  // 프로젝트 삭제 (updatedAt 갱신 필요 없음, 삭제 자체가 최신 반영)
  const handleDeleteProject = async (projectId) => {
    await deleteProject(projectId);
    setProjectMenuAnchor(null);
    setProjectMenuIdx(-1);
  };

  // 2차 메뉴 hover 핸들러
  const handleProjectMenuEnter = () => {
    clearTimeout(closeTimer.current);
    setProjectMenuHover(true);
  };
  const handleProjectMenuLeave = () => {
    closeTimer.current = setTimeout(() => setProjectMenuHover(false), 120);
  };
  const handleProjectSubMenuEnter = () => {
    clearTimeout(closeTimer.current);
    setProjectSubMenuHover(true);
  };
  const handleProjectSubMenuLeave = () => {
    closeTimer.current = setTimeout(() => setProjectSubMenuHover(false), 120);
  };

  return {
    projects, setProjects,
    projectModalOpen, setProjectModalOpen,
    newProjectName, setNewProjectName,
    projectHover, setProjectHover,
    projectMenuAnchor, setProjectMenuAnchor,
    projectMenuIdx, setProjectMenuIdx,
    historyMenuAnchor, setHistoryMenuAnchor,
    historyMenuIdx, setHistoryMenuIdx,
    projectSubMenuAnchor, setProjectSubMenuAnchor,
    projectSubMenuOpen, setProjectSubMenuOpen,
    projectSubMenuRefs, projectMenuRefs, historyMenuRefs, projectMenuItemRef,
    projectSubMenuHover, setProjectSubMenuHover,
    projectMenuHover, setProjectMenuHover,
    closeTimer,
    handleAddProject,
    handleDeleteProject,
    handleAddHistoryToProject,
    handleProjectMenuEnter,
    handleProjectMenuLeave,
    handleProjectSubMenuEnter,
    handleProjectSubMenuLeave,
    updateProjectName
  };
} 